import React from 'react';
import {
  Badge, Box, Button, Flex, IconButton, Input, InputGroup, InputLeftElement, Text,
} from '@chakra-ui/react';
import { CloseIcon, SearchIcon } from '../../icons';
import { countWord, initialsFor } from '../../utils/tenantAccess';
import StatusAlert from '../ElementaryComponents/StatusAlert';
import { EmptyState, Panel, StepFooter } from './TenantAccessLayout';

const UserTab = ({
  query,
  onQueryChange,
  results,
  isFetching,
  isError,
  error,
  selected,
  onAdd,
  onRemove,
  onClear,
  canContinue,
  onContinue,
}) => {
  const trimmed = query.trim();
  let resultsBlock = (
    <Text fontSize="sm" color="gray.600">
      Start typing to find users. Only people who already have an Exchange account appear here.
    </Text>
  );
  if (isFetching) {
    resultsBlock = <StatusAlert status="loading" alert_title="Searching users" />;
  } else if (isError) {
    resultsBlock = <StatusAlert status="error" alert_title="Could not search users" error={error} />;
  } else if (trimmed && results) {
    resultsBlock = results.length ? (
      <Box borderWidth="1px" borderRadius="md" overflow="hidden">
        <Flex px={3} py={2} bg="gray.50" borderBottomWidth="1px" fontSize="xs" fontWeight="600" color="gray.600">
          <Text>{countWord(results.length, 'result')}</Text>
        </Flex>
        {results.map((user) => {
          const added = selected.some((item) => item.id === user.id);
          return (
            <Flex key={user.id} align="center" gap={3} px={3} py={2} borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }}>
              <Flex boxSize="32px" borderRadius="full" bg="blue.50" color="blue.900" align="center" justify="center" fontSize="xs" fontWeight="700" flexShrink={0}>
                {initialsFor(user.first_name, user.last_name)}
              </Flex>
              <Box flex="1" minW="0">
                <Text fontWeight="600">{`${user.first_name} ${user.last_name}`}</Text>
                <Text fontSize="xs" color="gray.600">{`${user.email} · ${countWord(user.tenant_count, 'tenant')}`}</Text>
              </Box>
              {added ? (
                <Badge colorScheme="green">Added</Badge>
              ) : (
                <Button size="sm" variant="outline" colorScheme="blue" onClick={() => onAdd(user)}>Add</Button>
              )}
            </Flex>
          );
        })}
      </Box>
    ) : (
      <Box borderWidth="1px" borderRadius="md">
        <EmptyState
          title={`No users match “${query}”`}
          text="Check the spelling or try their email. This tool links existing accounts only, so the person needs an Exchange account first."
        />
      </Box>
    );
  }

  const selectedBlock = selected.length ? (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <Flex px={3} py={2} bg="gray.50" borderBottomWidth="1px" justify="space-between" align="center">
        <Text fontSize="xs" fontWeight="600" color="gray.600">{`Selected users (${selected.length})`}</Text>
        <Button variant="link" size="sm" colorScheme="blue" onClick={onClear}>Clear all</Button>
      </Flex>
      {selected.map((user) => (
        <Flex key={user.id} align="center" gap={3} px={3} py={2} borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }}>
          <Flex boxSize="32px" borderRadius="full" bg="blue.50" color="blue.900" align="center" justify="center" fontSize="xs" fontWeight="700" flexShrink={0}>
            {initialsFor(user.first_name, user.last_name)}
          </Flex>
          <Box flex="1" minW="0">
            <Text fontWeight="600">{`${user.first_name} ${user.last_name}`}</Text>
            <Text fontSize="xs" color="gray.600">{user.email}</Text>
          </Box>
          <Text fontSize="xs" color="gray.600">{`In ${countWord(user.tenant_count, 'tenant')}`}</Text>
          <IconButton
            aria-label={`Remove ${user.first_name}`}
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={() => onRemove(user.id)}
          />
        </Flex>
      ))}
    </Box>
  ) : (
    <Box borderWidth="1px" borderRadius="md">
      <EmptyState
        title="No users selected yet"
        text="Search above and add one or more people. Everyone you add gets the same tenants and app roles."
      />
    </Box>
  );

  return (
    <Panel
      title="Who are you adding?"
      subtitle="Find people by first name, last name, or email."
      footer={(
        <StepFooter
          hint={canContinue ? '' : 'Add at least one user to continue.'}
          canContinue={canContinue}
          onContinue={onContinue}
        />
      )}
    >
      <Box>
        <Text as="label" htmlFor="userQuery" fontWeight="600" fontSize="sm" display="block" mb={2}>Find users</Text>
        <InputGroup>
          <InputLeftElement pointerEvents="none"><SearchIcon color="gray.500" /></InputLeftElement>
          <Input
            id="userQuery"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="e.g. Maria, Delgado, or mdelgado@bayouridgeisd.org"
            autoComplete="off"
          />
        </InputGroup>
      </Box>
      {resultsBlock}
      {selectedBlock}
    </Panel>
  );
};

export default UserTab;
